if (!customElements.get('nosht-shop-by-need')) {
  customElements.define(
    'nosht-shop-by-need',
    class NoshtShopByNeed extends HTMLElement {
      connectedCallback() {
        queueMicrotask(() => this.init());
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.initialized = false;
      }

      init() {
        if (!this.isConnected || this.initialized) return;

        this.tabs = Array.from(this.querySelectorAll('[role="tab"][data-tab-id]')).filter((tab) =>
          this.querySelector(`#${tab.getAttribute('aria-controls')}`)
        );
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"][data-panel-id]'));

        if (this.tabs.length === 0 || this.panels.length === 0) return;

        this.controller = new AbortController();
        const listenerOptions = { signal: this.controller.signal };

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick, listenerOptions);
          tab.addEventListener('keydown', this.onTabKeydown, listenerOptions);
        });
        this.addEventListener('click', this.onReducedMotionArrowClick, { ...listenerOptions, capture: true });
        document.addEventListener('shopify:block:select', this.onBlockSelect, listenerOptions);

        const initialTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        this.activateTab(initialTab);
        this.setAttribute('data-tabs-ready', '');
        this.initialized = true;
      }

      onTabClick = (event) => {
        this.activateTab(event.currentTarget, { scrollTabIntoView: true });
      };

      onTabKeydown = (event) => {
        const currentIndex = this.tabs.indexOf(event.currentTarget);
        if (currentIndex < 0) return;

        const isRtl = getComputedStyle(this).direction === 'rtl';
        let nextIndex = currentIndex;

        switch (event.key) {
          case 'ArrowRight':
            nextIndex = isRtl ? currentIndex - 1 : currentIndex + 1;
            break;
          case 'ArrowLeft':
            nextIndex = isRtl ? currentIndex + 1 : currentIndex - 1;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this.tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        nextIndex = (nextIndex + this.tabs.length) % this.tabs.length;
        const nextTab = this.tabs[nextIndex];
        this.activateTab(nextTab, { focusTab: true, scrollTabIntoView: true });
      };

      onReducedMotionArrowClick = (event) => {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const arrow = event.target.closest('[data-gridy-arrow]');
        const slider = arrow?.closest('gridy-slider');
        const track = slider?.track;
        if (!arrow || !slider || !this.contains(slider) || typeof slider.scrollByIPR !== 'function') return;
        if (!track || typeof track.scrollTo !== 'function') return;

        event.preventDefault();
        event.stopPropagation();

        const nativeScrollTo = track.scrollTo;
        track.scrollTo = (...args) => {
          if (typeof args[0] === 'object' && args[0] !== null) {
            args[0] = { ...args[0], behavior: 'auto' };
          }
          return nativeScrollTo.apply(track, args);
        };

        try {
          slider.scrollByIPR(arrow.dataset.direction);
        } finally {
          track.scrollTo = nativeScrollTo;
        }
      };

      onBlockSelect = (event) => {
        const sectionId = String(event.detail?.sectionId || '');
        if (sectionId && sectionId !== String(this.dataset.sectionId)) return;

        const blockId = String(event.detail?.blockId || event.target?.dataset?.tabId || '');
        if (!blockId) return;

        const selectedTab = this.tabs.find((tab) => tab.dataset.tabId === blockId);
        if (selectedTab) {
          this.activateTab(selectedTab, { scrollTabIntoView: true });
        }
      };

      activateTab(tab, { focusTab = false, scrollTabIntoView = false } = {}) {
        if (!tab || !this.tabs.includes(tab)) return;

        const activeId = tab.dataset.tabId;
        const activePanel = this.panels.find((panel) => panel.dataset.panelId === activeId);

        this.hydratePanel(activePanel);

        this.tabs.forEach((candidate) => {
          const isActive = candidate === tab;
          candidate.setAttribute('aria-selected', String(isActive));
          candidate.tabIndex = isActive ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          const isActive = panel.dataset.panelId === activeId;
          panel.hidden = !isActive;
          panel.dataset.active = String(isActive);
        });

        if (focusTab) tab.focus({ preventScroll: true });

        if (scrollTabIntoView) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          tab.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }

        if (activePanel) {
          requestAnimationFrame(() => {
            activePanel.querySelectorAll('gridy-slider').forEach((slider) => {
              slider.querySelector('.gridy-track')?.dispatchEvent(new Event('scroll', { bubbles: false }));
            });
          });
        }
      }

      hydratePanel(panel) {
        const template = panel?.querySelector(':scope > template[data-nosht-deferred-panel-content]');
        if (!template) return;

        template.replaceWith(template.content.cloneNode(true));
        panel.removeAttribute('data-panel-deferred');
      }
    }
  );
}
